// FIXME: This should be exported from the ra-core package
type CanAccessParams<
  RecordType extends Record<string, any> = Record<string, any>,
> = {
  action: string;
  resource: string;
  record?: RecordType;
};

/**
 * Permission ids are "<resource>.<action>", matching the `permissions` table.
 * The UI and the RLS policies resolve the same strings, so what the interface
 * offers and what the database allows cannot drift apart.
 */
export const permissionId = (resource: string, action: string) =>
  `${resource}.${action}`;

/**
 * ra-core asks for actions this app models under a different name.
 * Anything not listed maps straight through.
 */
const ACTION_ALIASES: Record<string, string> = {
  read: "show",
  write: "edit",
  update: "edit",
  remove: "delete",
  // Exporting is a bulk read of everything the list can reach, which belongs
  // with managing a resource rather than with viewing your own row of it. A
  // client can see their project and cannot export a table of projects.
  // Without this it resolves to a permission that does not exist -- and an
  // absent permission is denied to everyone, admins included.
  export: "edit",
};

/**
 * Resources whose table name differs from the permission they are governed by.
 * "deals" is aliased to "leads" so the Phase 4 table rename needs no permission
 * changes; the note tables share one permission because they are the same
 * concept attached to different parents.
 */
const RESOURCE_ALIASES: Record<string, string> = {
  deals: "leads",
  contact_notes: "notes",
  comments: "notes",
  deal_notes: "notes",
};

/**
 * Resources that exist only in the UI and carry no rows of their own.
 * They are gated on a permission belonging to another resource.
 */
const VIRTUAL_RESOURCES: Record<string, string> = {
  configuration: "config.edit",
  sales: "users.list",
  dashboard: "dashboard.view",
  // activity_log is the feed the dashboard is built from, and every role gets
  // a dashboard. Which rows they see is an RLS question, not a UI one: the
  // view is security_invoker=on, so it inherits the underlying table policies.
  activity_log: "dashboard.view",
  // Everyone gets their own notifications. RLS restricts the rows to the
  // recipient, so there is nothing here for a permission to decide.
  notifications: "dashboard.view",
  // The feed itself is open to anyone with a dashboard; what they receive is
  // decided by RLS, where dashboard.view_all is what widens it to the whole hub.
  activity_events: "dashboard.view",
  // The role catalog is reference data anyone managing users needs to read;
  // the assignment tables are writable only with users.manage_roles, which the
  // RLS policies enforce independently of this check.
  roles: "users.list",
  permissions: "users.list",
  role_permissions: "users.list",
  user_roles: "users.manage_roles",
  user_permissions: "users.manage_roles",
};

export const canAccess = <
  RecordType extends Record<string, any> = Record<string, any>,
>(
  permissions: ReadonlySet<string>,
  params: CanAccessParams<RecordType>,
) => {
  const virtual = VIRTUAL_RESOURCES[params.resource];
  if (virtual) return permissions.has(virtual);

  const resource = RESOURCE_ALIASES[params.resource] ?? params.resource;
  const action = ACTION_ALIASES[params.action] ?? params.action;
  return permissions.has(permissionId(resource, action));
};
